import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks.js';
import { selectInstruments, selectIsInstrumentsFetched, removeInstrument, addInstruments, updateInstrumentPrice } from '../../store/instrumentsSlice.js';
import { openChart } from '../../store/chartSlice.js';
import { ProviderFactory } from '../../services/providers';
import { apiClient } from '../../services/api';
import { wsClient } from '../../services/websocket';
import OrderForm from '../OrderForm.js';
import ClickRipple from '../ClickRipple.js';

const MarketWatch = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const instruments = useAppSelector(selectInstruments);
    const isFetched = useAppSelector(selectIsInstrumentsFetched);
    const connectedDataFeedProviders = 'kite'
    const dataFeedProvider = connectedDataFeedProviders;
    const [orderFormSymbol, setOrderFormSymbol] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{ symbol: string; name: string; exchange: string; type: string }>>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [isLoadingWatchlist, setIsLoadingWatchlist] = useState(true);

    // Fetch watchlist from backend on component mount - only if not already fetched
    useEffect(() => {
        // Skip fetch if already fetched
        if (isFetched) {
            console.log('[MarketWatch] Watchlist already fetched, skipping API call');
            setIsLoadingWatchlist(false);
            return;
        }

        const fetchWatchlist = async () => {
            try {
                setIsLoadingWatchlist(true);
                const response = await apiClient.get<{
                    success: boolean;
                    data: {
                        items: Array<string>;
                    };
                    message: string;

                }>('/watchlist');
                console.log('[MarketWatch] Fetched watchlist response:', response);

                // Add fetched instruments to Redux store
                if (response.success && response.data.items.length > 0) {
                    const instruments = response.data.items;
                    console.log('[MarketWatch] Fetched watchlist instruments:', instruments);

                    const instrumentsResponse = await apiClient.get<{
                        success: boolean;
                        message: string;
                        data: {
                            instruments: Array<{
                                tradingSymbol: string;
                            }>;
                        }
                    }>('/instruments/', { params: { symbols: instruments } });

                    console.log('[MarketWatch] Fetched instruments response:', instrumentsResponse);

                    if (!instrumentsResponse.success) {
                        console.error('[MarketWatch] Failed to fetch instruments details:', instrumentsResponse);
                        return;
                    }

                    const newInstruments = instrumentsResponse.data.instruments.map((instrument, index) => ({
                        id: `${instrument.tradingSymbol}-${Date.now()}-${index}`,
                        symbol: instrument.tradingSymbol,
                        name: '', // Name can be fetched separately if needed
                        price: 0,
                        change: 0,
                        changePercent: 0
                    }));

                    // Use bulk add action to add all instruments at once and set isFetched flag
                    dispatch(addInstruments(newInstruments));

                    console.log('[MarketWatch] Added instruments to store:', newInstruments);
                } else {
                    // Mark as fetched even if empty to prevent repeated API calls
                    dispatch(addInstruments([]));
                }
            } catch (error) {
                console.error('[MarketWatch] Error fetching watchlist:', error);
            } finally {
                setIsLoadingWatchlist(false);
            }
        };

        fetchWatchlist();
    }, [dispatch, isFetched]);

    // Subscribe to real-time price updates
    useEffect(() => {
        // Only subscribe if we have instruments
        if (instruments.length === 0) {
            console.log('[MarketWatch] No instruments to subscribe to');
            return;
        }

        const symbols = instruments.map(i => i.symbol);
        console.log('[MarketWatch] Subscribing to WebSocket updates for symbols:', symbols);

        // Subscribe to symbols via WebSocket
        wsClient.subscribe(symbols);

        // Set up tick handler to update prices
        const unsubscribeTicks = wsClient.onChannelData('ticks', (ticks) => {
            console.log('[MarketWatch] Received ticks:', ticks);

            // Update instrument prices based on ticks
            ticks.forEach((tick: any) => {
                const symbol = tick.symbol || tick.tradingSymbol;
                const lastPrice = tick.lp || tick.last_price;

                if (!symbol || !lastPrice) return;

                // Find matching instrument and update in Redux
                const matchingInstrument = instruments.find(i => i.symbol === symbol);
                if (matchingInstrument) {
                    // Calculate change (comparing with current stored price)
                    const previousPrice = matchingInstrument.price || lastPrice;
                    const change = lastPrice - previousPrice;
                    const changePercent = previousPrice > 0
                        ? (change / previousPrice) * 100
                        : 0;

                    console.log(`[MarketWatch] Updating ${symbol}: ${previousPrice} → ${lastPrice} (${changePercent > 0 ? '+' : ''}${changePercent.toFixed(2)}%)`);

                    // Dispatch update action
                    dispatch(updateInstrumentPrice({
                        id: matchingInstrument.id,
                        price: lastPrice,
                        change,
                        changePercent
                    }));
                }
            });
        });

        // Cleanup: unsubscribe when component unmounts or instruments change
        return () => {
            if (symbols.length > 0) {
                console.log('[MarketWatch] Unsubscribing from symbols:', symbols);
                wsClient.unsubscribe(symbols);
                unsubscribeTicks();
            }
        };
    }, [instruments.map(i => i.symbol).join(',')]); // Re-subscribe when instruments change

    // Search for instruments
    useEffect(() => {
        if (searchQuery.length < 1) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }

        const searchTimer = setTimeout(async () => {
            setIsSearching(true);
            try {
                // ProviderFactory is automatically synced with Redux
                const dataFeed = ProviderFactory.getDataFeedProvider(dataFeedProvider as any);

                if (!dataFeed) {
                    console.warn('[MarketWatch] No data feed provider configured. Please connect a provider in Account settings.');
                    setSearchResults([]);
                    setIsSearching(false);
                    return;
                }

                // Connect if not already connected
                if (!dataFeed.isConnected()) {
                    console.error('[MarketWatch] Data feed not connected');
                    return;
                }

                // const results = await dataFeed.searchSymbols(searchQuery);
                // setSearchResults(results);
                setShowResults(true);
            } catch (error) {
                console.error('[MarketWatch] Search error:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300); // Debounce 300ms

        return () => clearTimeout(searchTimer);
    }, [searchQuery]);

    const handleAddInstrument = async (result: typeof searchResults[0]) => {
        // Check if already in watchlist
        if (instruments.some(i => i.symbol === result.symbol)) {
            return;
        }

        try {
            // ProviderFactory is automatically synced with Redux
            const dataFeed = ProviderFactory.getDataFeedProvider(dataFeedProvider as any);

            if (!dataFeed) {
                console.error('[MarketWatch] No data feed provider configured. Please connect a provider in Account settings.');
                return;
            }

            // Get current quote for the instrument
            // const quote = await dataFeed.getMarketQuote(result.symbol);

            // // Add to Redux store
            // dispatch(addInstrument({
            //     id: `${result.symbol}-${Date.now()}`,
            //     symbol: result.symbol,
            //     name: result.name,
            //     price: quote.price,
            //     change: quote.change,
            //     changePercent: quote.changePercent
            // }));

            // Clear search
            setSearchQuery('');
            setSearchResults([]);
            setShowResults(false);
        } catch (error) {
            console.error('[MarketWatch] Error adding instrument:', error);
        }
    };

    const handleOpenChart = (symbol: string) => {
        dispatch(openChart(symbol));
    };

    const handleBuy = (symbol: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setOrderFormSymbol(symbol);
    };

    const handleSell = (symbol: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setOrderFormSymbol(symbol);
    };

    const handleDelete = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(removeInstrument(id));
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {
                instruments.length > 0 ?
                    <>
                        {/* Search Bar */}
                        <div style={{
                            padding: 'var(--spacing-sm)',
                            borderBottom: '1px solid var(--color-border-light)',
                            backgroundColor: 'var(--color-surface)',
                            position: 'relative'
                        }}>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search symbols (e.g., AAPL, MSFT)..."
                                    style={{
                                        width: '100%',
                                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                                        paddingLeft: '32px',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border-light)',
                                        backgroundColor: 'var(--color-surface)',
                                        fontSize: '0.875rem',
                                        color: 'var(--color-text-primary)',
                                        outline: 'none',
                                        transition: 'border-color 200ms ease-in-out'
                                    }}
                                    onFocus={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--color-primary-600)';
                                        if (searchResults.length > 0) setShowResults(true);
                                    }}
                                    onBlur={() => {
                                        // Delay to allow click on results
                                        setTimeout(() => setShowResults(false), 200);
                                    }}
                                />
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--color-text-secondary)"
                                    style={{
                                        position: 'absolute',
                                        left: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <circle cx="11" cy="11" r="8" strokeWidth="2" />
                                    <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                {isSearching && (
                                    <div style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid var(--color-border-light)',
                                        borderTopColor: 'var(--color-primary-600)',
                                        borderRadius: '50%',
                                        animation: 'spin 0.6s linear infinite'
                                    }} />
                                )}
                            </div>

                            {/* Search Results Dropdown */}
                            {showResults && searchResults.length > 0 && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 'var(--spacing-sm)',
                                    right: 'var(--spacing-sm)',
                                    marginTop: '4px',
                                    backgroundColor: 'var(--color-surface)',
                                    border: '1px solid var(--color-border-light)',
                                    borderRadius: 'var(--radius-md)',
                                    boxShadow: 'var(--shadow-lg)',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 10
                                }}>
                                    {searchResults.map((result, index) => {
                                        const isInWatchlist = instruments.some(i => i.symbol === result.symbol);
                                        return (
                                            <div
                                                key={`${result.symbol}-${index}`}
                                                onClick={() => !isInWatchlist && handleAddInstrument(result)}
                                                style={{
                                                    padding: 'var(--spacing-sm)',
                                                    borderBottom: index < searchResults.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                                                    cursor: isInWatchlist ? 'default' : 'pointer',
                                                    opacity: isInWatchlist ? 0.5 : 1,
                                                    transition: 'background-color 200ms ease-in-out'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isInWatchlist) {
                                                        e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 'var(--spacing-sm)'
                                                }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: '700',
                                                            color: 'var(--color-text-primary)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--spacing-xs)'
                                                        }}>
                                                            {result.symbol}
                                                            {isInWatchlist && (
                                                                <span style={{
                                                                    fontSize: '0.6875rem',
                                                                    fontWeight: '500',
                                                                    color: 'var(--color-success-600)',
                                                                    backgroundColor: 'var(--color-success-50)',
                                                                    padding: '2px 6px',
                                                                    borderRadius: 'var(--radius-sm)'
                                                                }}>
                                                                    Added
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: 'var(--color-text-secondary)',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}>
                                                            {result.name}
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.6875rem',
                                                        color: 'var(--color-text-secondary)',
                                                        flexShrink: 0
                                                    }}>
                                                        {result.exchange}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <style>{`
                    @keyframes spin {
                        to { transform: translateY(-50%) rotate(360deg); }
                    }
                `}</style>
                        </div>

                        {/* Instruments Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gridAutoRows: '70px',
                            gap: 0,
                            flex: 1,
                            overflow: 'auto',
                            border: '1px solid var(--color-border-light)',
                            alignContent: 'start'
                        }}>
                            {instruments.map((instrument) => (
                                <ClickRipple
                                    key={instrument.id}
                                    onClick={() => handleOpenChart(instrument.symbol)}
                                    className="instrument-card"
                                    rippleColor={instrument.change >= 0 ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}
                                    rippleDuration={700}
                                    style={{
                                        position: 'relative',
                                        padding: 'var(--spacing-xs)',
                                        borderRight: '1px solid var(--color-border-light)',
                                        borderBottom: '1px solid var(--color-border-light)',
                                        cursor: 'pointer',
                                        backgroundColor: 'var(--color-surface)',
                                        transition: 'all 200ms ease-in-out',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                        height: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                    }}
                                >
                                    {/* Main Content - Symbol/Name and Price/Change */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 'var(--spacing-xs)',
                                        alignItems: 'flex-start'
                                    }}>
                                        {/* Symbol and Name */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '0.875rem',
                                                fontWeight: '700',
                                                color: 'var(--color-text-primary)',
                                                lineHeight: 1.2
                                            }}>
                                                {instrument.symbol}
                                            </div>
                                            <div style={{
                                                fontSize: '0.6875rem',
                                                color: 'var(--color-text-secondary)',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                lineHeight: 1.3,
                                                marginTop: '1px'
                                            }}>
                                                {instrument.name}
                                            </div>
                                        </div>

                                        {/* Price and Change */}
                                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                            <div style={{
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                color: 'var(--color-text-primary)',
                                                lineHeight: 1.2
                                            }}>
                                                {instrument.price.toFixed(2)}
                                            </div>
                                            <div style={{
                                                fontSize: '0.6875rem',
                                                fontWeight: '500',
                                                color: instrument.change >= 0
                                                    ? 'var(--color-success-600)'
                                                    : 'var(--color-danger-600)',
                                                lineHeight: 1.3,
                                                marginTop: '1px'
                                            }}>
                                                {instrument.change >= 0 ? '+' : ''}{instrument.change.toFixed(2)} ({instrument.changePercent >= 0 ? '+' : ''}{instrument.changePercent.toFixed(2)}%)
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons - Show on hover */}
                                    <div
                                        className="action-buttons"
                                        style={{
                                            display: 'flex',
                                            gap: '4px',
                                            marginTop: 'auto',
                                            opacity: 0,
                                            transform: 'translateY(4px)',
                                            transition: 'all 200ms ease-in-out',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <button
                                            onClick={(e) => handleBuy(instrument.symbol, e)}
                                            style={{
                                                flex: 1,
                                                padding: '4px',
                                                backgroundColor: 'var(--color-success-600)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: 'var(--radius-sm)',
                                                cursor: 'pointer',
                                                fontSize: '0.6875rem',
                                                fontWeight: '700',
                                                transition: 'background-color 200ms ease-in-out'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-success-700)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-success-600)';
                                            }}
                                        >
                                            B
                                        </button>
                                        <button
                                            onClick={(e) => handleSell(instrument.symbol, e)}
                                            style={{
                                                flex: 1,
                                                padding: '4px',
                                                backgroundColor: 'var(--color-danger-600)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: 'var(--radius-sm)',
                                                cursor: 'pointer',
                                                fontSize: '0.6875rem',
                                                fontWeight: '700',
                                                transition: 'background-color 200ms ease-in-out'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-danger-700)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-danger-600)';
                                            }}
                                        >
                                            S
                                        </button>
                                        <button
                                            onClick={(e) => handleDelete(instrument.id, e)}
                                            style={{
                                                flex: 1,
                                                padding: '4px',
                                                backgroundColor: 'var(--color-gray-600)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: 'var(--radius-sm)',
                                                cursor: 'pointer',
                                                fontSize: '0.6875rem',
                                                fontWeight: '700',
                                                transition: 'background-color 200ms ease-in-out',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-danger-600)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-gray-600)';
                                            }}
                                        >
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <line x1="18" y1="6" x2="6" y2="18" strokeWidth="3" strokeLinecap="round" />
                                                <line x1="6" y1="6" x2="18" y2="18" strokeWidth="3" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>

                                    <style>{`
                            .instrument-card:hover .action-buttons {
                                opacity: 1 !important;
                                transform: translateY(0) !important;
                                pointer-events: auto !important;
                            }
                        `}</style>
                                </ClickRipple>
                            ))}
                        </div>

                        {/* Order Form Modal */}
                        {orderFormSymbol && (
                            <OrderForm
                                symbol={orderFormSymbol}
                                onClose={() => setOrderFormSymbol(null)}
                            />
                        )}
                    </>

                    : <>
                        {/* Empty State */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            padding: 'var(--spacing-xl)',
                            textAlign: 'center',
                            gap: 'var(--spacing-md)'
                        }}>
                            {isLoadingWatchlist ? (
                                <>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '3px solid var(--color-border-light)',
                                        borderTopColor: 'var(--color-primary-600)',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }} />
                                    <p style={{
                                        fontSize: '0.875rem',
                                        color: 'var(--color-text-secondary)',
                                        margin: 0
                                    }}>
                                        Loading your watchlist...
                                    </p>
                                </>
                            ) : (
                                <>
                                    <svg
                                        width="64"
                                        height="64"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="var(--color-text-tertiary)"
                                        strokeWidth="1.5"
                                    >
                                        <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div>
                                        <h3 style={{
                                            fontSize: '1.125rem',
                                            fontWeight: '600',
                                            color: 'var(--color-text-primary)',
                                            margin: '0 0 var(--spacing-xs) 0'
                                        }}>
                                            No Instruments in Watchlist
                                        </h3>
                                        <p style={{
                                            fontSize: '0.875rem',
                                            color: 'var(--color-text-secondary)',
                                            margin: 0,
                                            maxWidth: '320px'
                                        }}>
                                            Configure your data feed provider to start tracking market instruments
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => navigate('/account')}
                                        style={{
                                            padding: 'var(--spacing-sm) var(--spacing-lg)',
                                            backgroundColor: 'var(--color-primary-600)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'background-color 200ms ease-in-out',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--spacing-xs)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="3" />
                                            <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24" strokeLinecap="round" />
                                        </svg>
                                        Configure Data Feed
                                    </button>
                                </>
                            )}
                        </div>
                    </>



            }

        </div>
    );
}

export default MarketWatch;

